import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import useQuery from './useQuery';
import { useCompanyStore } from '../stores';
import { isLinkInvalid } from '../utils/linkSession';

export const LINK_SESSION_STATUS = {
	LOADING: 'loading',
	LINK_EXPIRED: 'linkExpired',
	NOT_RESCHEDULABLE: 'notReschedulable',
	LOAD_ERROR: 'loadError'
};

const useAgendeaiLinkSession = ({ findLink, findSettings, findScheduleSettings, getLinkErrorStatus, onLoaded }) => {
	const params = useParams();
	const query = useQuery();
	const code = useMemo(() => params.code || query.get('token'), [params.code, query]);

	const { setCompanySettings } = useCompanyStore();

	const [status, setStatus] = useState(LINK_SESSION_STATUS.LOADING);

	const load = useCallback(async () => {
		setStatus(LINK_SESSION_STATUS.LOADING);

		if (!code) {
			setStatus(LINK_SESSION_STATUS.LINK_EXPIRED);

			return;
		}

		const filter = { token: code };

		const [linkResult, settingsResult, scheduleSettingsResult] = await Promise.all([
			findLink(filter),
			findSettings(filter),
			findScheduleSettings ? findScheduleSettings(filter) : null
		]);

		const results = [linkResult, settingsResult, scheduleSettingsResult].filter(Boolean);

		if (results.some(result => isLinkInvalid(result.error))) {
			setStatus(LINK_SESSION_STATUS.LINK_EXPIRED);

			return;
		}

		if (linkResult.error) {
			setStatus(getLinkErrorStatus?.(linkResult.error) || LINK_SESSION_STATUS.LOAD_ERROR);

			return;
		}

		const hasScheduleSettingsError = scheduleSettingsResult && (scheduleSettingsResult.error || !scheduleSettingsResult.data);

		if (settingsResult.error || !settingsResult.data?.token || hasScheduleSettingsError) {
			setStatus(LINK_SESSION_STATUS.LOAD_ERROR);

			return;
		}

		if (settingsResult.data.name) {
			document.title = settingsResult.data.name;
		}

		setCompanySettings({
			settings: settingsResult.data,
			...(scheduleSettingsResult && { scheduleSettings: scheduleSettingsResult.data })
		});

		localStorage.setItem('token', settingsResult.data.token);

		onLoaded({ code, data: linkResult.data });
	}, [code, findLink, findSettings, findScheduleSettings, getLinkErrorStatus, onLoaded, setCompanySettings]);

	useEffect(() => {
		load();
	}, [load]);

	return { status, reload: load };
};

export default useAgendeaiLinkSession;
